/**
 * GlobalUpgradeFlow — monta UM PlanPickerDialog + UM BillingCheckoutDialog
 * compartilhados pelo app inteiro. Qualquer componente em qualquer página
 * abre o fluxo via `useUpgradeStore.openUpgrade()` — sem precisar montar
 * um picker próprio nem navegar pra outra rota.
 *
 * Mount: dentro do AuthRoot (uma única vez). Não monta na rota /pricing
 * (que tem seu próprio fluxo público) nem em rotas não-autenticadas.
 *
 * Tenant com assinatura paga ATIVA que escolhe outro plano vai pro
 * UpgradePlanDialog (POST /billing/upgrade — troca instantânea na Asaas,
 * sem novo checkout). Sem sub ativa → BillingCheckoutDialog normal.
 *
 * Por que não morar dentro do AuthStore: separação clara — auth lida
 * com sessão; upgrade store lida com gateway de venda. Mantém cada
 * store focado.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useUpgradeStore } from '@/store/upgradeStore';
import { PlanPickerDialog } from './PlanPickerDialog';
import { BillingCheckoutDialog, type BillingSku } from './BillingCheckoutDialog';
import { UpgradePlanDialog, type UpgradeTarget } from './UpgradePlanDialog';
import { useAuthStore } from '@/store/authStore';
import { useBillingInfo, usePlans } from '@/api/queries';
import { purchasesAllowed } from '@/lib/purchases';
import { ResponsiveDialog } from '@/components/ui/responsive-dialog';
import { Button } from '@/components/ui/button';

/**
 * Versão do fluxo pro app nativo (Google Play): explicar sem vender.
 * Todos os gatilhos de upgrade espalhados pelo app (PlanLock, radial do
 * editor, analytics lock…) continuam abrindo o upgradeStore — aqui, em
 * vez de picker + checkout, o usuário vê um aviso neutro: sem preço,
 * sem CTA de compra e sem apontar pra fora do app (anti-steering).
 */
function NativePlanNotice() {
  const { t } = useTranslation();
  const open = useUpgradeStore((s) => s.open);
  const closeUpgrade = useUpgradeStore((s) => s.closeUpgrade);

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) closeUpgrade();
      }}
      size="sm"
      title={
        <span className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
            <Lock className="h-4 w-4" />
          </span>
          <span
            className="text-base font-semibold tracking-tight"
            style={{ color: 'var(--color-text)' }}
          >
            {t('billingUi:nativeGate.title')}
          </span>
        </span>
      }
      footer={
        <Button variant="primary" onClick={closeUpgrade}>
          {t('billingUi:nativeGate.ok')}
        </Button>
      }
    >
      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
        {t('billingUi:nativeGate.body')}
      </p>
    </ResponsiveDialog>
  );
}

export function GlobalUpgradeFlow() {
  const open = useUpgradeStore((s) => s.open);
  const closeUpgrade = useUpgradeStore((s) => s.closeUpgrade);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [checkoutSku, setCheckoutSku] = useState<BillingSku | null>(null);
  const [upgradeTarget, setUpgradeTarget] = useState<UpgradeTarget | null>(null);
  const { checkAuth } = useAuthStore();
  const { data: billing } = useBillingInfo();
  const { data: plansData } = usePlans();

  // No-op em landing/login/pricing — esses usuários não têm tenant
  // pra fechar checkout. Picker tentaria fetch /billing/plans com
  // 401 e nada renderiza utilmente. Mantém o componente seguro de
  // montar globalmente.
  if (!isAuthenticated) return null;

  // App nativo: sem venda (política de billing do Google Play).
  if (!purchasesAllowed()) return <NativePlanNotice />;

  const currentPlan = billing?.data?.tenant?.plan ?? 'free';
  const hasActivePaidSub =
    billing?.data?.subscription?.status === 'active' &&
    billing.data.subscription.plan !== 'free';

  return (
    <>
      <PlanPickerDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) closeUpgrade();
        }}
        onPick={(sku) => {
          if (hasActivePaidSub && sku.slug !== currentPlan) {
            setUpgradeTarget({ slug: sku.slug, name: sku.name, priceCents: sku.priceCents });
            return;
          }
          setCheckoutSku(sku);
        }}
      />
      <UpgradePlanDialog
        open={!!upgradeTarget}
        onOpenChange={(o) => !o && setUpgradeTarget(null)}
        target={upgradeTarget}
        currentPlan={currentPlan}
        currentPriceCents={
          plansData?.data?.find((p) => p.slug === currentPlan)?.price ?? null
        }
        onSuccess={() => {
          // Reload tenant.plan/limits — banners de upgrade somem,
          // features destravam imediatamente sem precisar refresh.
          checkAuth();
        }}
      />
      <BillingCheckoutDialog
        open={!!checkoutSku}
        onOpenChange={(o) => !o && setCheckoutSku(null)}
        sku={checkoutSku}
        onSuccess={() => {
          // Reload tenant.plan/limits — banners de upgrade somem,
          // features destravam imediatamente sem precisar refresh.
          checkAuth();
        }}
      />
    </>
  );
}
